import { ExcelFileRecord } from './excel-export.types';

const DB_NAME = 'ai-chat-excel-files';
const DB_VERSION = 1;
const STORE_NAME = 'files';
const CREATED_AT_INDEX = 'createdAt';

/** Через сколько файл считается устаревшим и удаляется из браузера. */
const RECORD_TTL_MS = 7 * 24 * 60 * 60 * 1000;
/** Сколько последних файлов держим в памяти, чтобы не читать IndexedDB на каждый рендер. */
const MEMORY_CACHE_LIMIT = 20;

const memoryCache = new Map<string, ExcelFileRecord>();
let databasePromise: Promise<IDBDatabase | null> | null = null;

/** Сохраняет готовый файл: сначала в память, затем в IndexedDB (если доступна). */
export async function saveExcelFile(record: ExcelFileRecord): Promise<void> {
    rememberInMemory(record);

    const db = await openDatabase();

    if (!db) {
        return;
    }

    try {
        await runTransaction(db, 'readwrite', store => store.put(record));
        await pruneExpiredFiles(db);
    } catch (error) {
        // Файл уже доступен из памяти — падение IndexedDB не должно ломать сценарий.
        console.warn('Failed to persist excel file in IndexedDB:', error);
    }
}

/** Возвращает файл по id или null, если он не найден/уже удалён. */
export async function getExcelFile(id: string): Promise<ExcelFileRecord | null> {
    const cached = memoryCache.get(id);

    if (cached) {
        return cached;
    }

    const db = await openDatabase();

    if (!db) {
        return null;
    }

    try {
        const record = await requestToPromise<ExcelFileRecord | undefined>(
            db.transaction(STORE_NAME, 'readonly').objectStore(STORE_NAME).get(id),
        );

        if (!record) {
            return null;
        }

        rememberInMemory(record);
        return record;
    } catch (error) {
        console.warn('Failed to read excel file from IndexedDB:', error);
        return null;
    }
}

function rememberInMemory(record: ExcelFileRecord): void {
    memoryCache.delete(record.id);
    memoryCache.set(record.id, record);

    while (memoryCache.size > MEMORY_CACHE_LIMIT) {
        const oldestKey = memoryCache.keys().next().value;

        if (oldestKey === undefined) {
            break;
        }

        memoryCache.delete(oldestKey);
    }
}

function openDatabase(): Promise<IDBDatabase | null> {
    if (databasePromise) {
        return databasePromise;
    }

    databasePromise = new Promise<IDBDatabase | null>(resolve => {
        try {
            if (typeof indexedDB === 'undefined') {
                resolve(null);
                return;
            }

            const request = indexedDB.open(DB_NAME, DB_VERSION);

            request.onupgradeneeded = () => {
                const db = request.result;

                if (!db.objectStoreNames.contains(STORE_NAME)) {
                    const store = db.createObjectStore(STORE_NAME, { keyPath: 'id' });
                    store.createIndex(CREATED_AT_INDEX, CREATED_AT_INDEX);
                }
            };

            request.onsuccess = () => resolve(request.result);
            request.onerror = () => {
                console.warn('Failed to open excel files IndexedDB:', request.error);
                resolve(null);
            };
            request.onblocked = () => {
                console.warn('Excel files IndexedDB upgrade is blocked by another tab');
                resolve(null);
            };
        } catch (error) {
            console.warn('IndexedDB is unavailable for excel files:', error);
            resolve(null);
        }
    });

    return databasePromise;
}

function runTransaction(
    db: IDBDatabase,
    mode: IDBTransactionMode,
    action: (store: IDBObjectStore) => void,
): Promise<void> {
    return new Promise((resolve, reject) => {
        const transaction = db.transaction(STORE_NAME, mode);

        transaction.oncomplete = () => resolve();
        transaction.onerror = () => reject(transaction.error);
        transaction.onabort = () => reject(transaction.error);

        try {
            action(transaction.objectStore(STORE_NAME));
        } catch (error) {
            reject(error);
        }
    });
}

function requestToPromise<T>(request: IDBRequest<T>): Promise<T> {
    return new Promise((resolve, reject) => {
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
    });
}

/** Удаляет файлы старше TTL, чтобы не копить мусор в браузере. */
async function pruneExpiredFiles(db: IDBDatabase): Promise<void> {
    const cutoff = Date.now() - RECORD_TTL_MS;

    try {
        const keys = await requestToPromise(
            db.transaction(STORE_NAME, 'readonly')
                .objectStore(STORE_NAME)
                .index(CREATED_AT_INDEX)
                .getAllKeys(IDBKeyRange.bound(0, cutoff)),
        );

        if (!keys.length) {
            return;
        }

        await runTransaction(db, 'readwrite', store => {
            for (const key of keys) {
                store.delete(key);
            }
        });
    } catch (error) {
        console.warn('Failed to prune expired excel files:', error);
    }
}
